import React from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import "./css/PrivacyPolicy.css";

const RefundPolicy = () => {
  const pageTitle = "Refund Policy | Dhandha Karle";
  const metaDescription =
    "Understand the refund policy for Dhandha Karle's POS, inventory management, and invoice generator software. Learn about our terms for cancellations and refunds.";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta
          name="keywords"
          content="Dhandha Karle refund policy, POS software refund, inventory management refund, invoice generator refund, cancellation policy"
        />
        <meta name="author" content="Dhandha Karle Team" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dhandhakarle.com/refund-policy" />
        <meta
          property="og:image"
          content="https://dhandhakarle.com/og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <link rel="canonical" href="https://dhandhakarle.com/refund-policy" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <section className="section refund-policy">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="container"
        >
          <h2 style={{ textAlign: "center", color: "#2563eb" }}>
            Refund Policy
          </h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="card"
            style={{ backgroundColor: "#f3f4f6", padding: "2rem" }}
          >
            <h3>Our Commitment</h3>
            <p>
              At Dhandha Karle, we strive to provide exceptional POS, inventory
              management, and invoice generation software to meet your business
              needs. We offer a 15-day free trial for our Restaurant/Inventory POS
              Software and a free plan for our Invoice Generator to ensure you can
              evaluate our services risk-free. If you are not satisfied with our
              premium plans, we have outlined our refund policy below.
            </p>

            <h3>Refund Eligibility</h3>
            <ul>
              <li>
                <strong>Premium Plan (Restaurant/Inventory POS Software):</strong>{" "}
                You are eligible for a full refund within 7 days of purchasing the
                annual Premium Plan (₹4999/year) if you find the service does not
                meet your expectations. Refunds will not be issued after this
                7-day period.
              </li>
              <li>
                <strong>Premium Plan (Invoice Generator):</strong> You are
                eligible for a full refund within 7 days of purchasing the annual
                Premium Plan (₹999/year) if the service does not meet your needs.
                No refunds will be issued after this period.
              </li>
              <li>
                <strong>Free Trial and Free Plan:</strong> As these are free
                services, no refunds are applicable.
              </li>
            </ul>

            <h3>Refund Process</h3>
            <p>
              To request a refund, please contact our support team at{" "}
              <a href="mailto:support@dhandhakarle.com">
                support@dhandhakarle.com
              </a>{" "}
              within the 7-day refund period. Include your purchase details, such
              as the order number and the email used for the purchase. Our team
              will review your request and process the refund within 5-7 business
              days, provided it meets the eligibility criteria. Refunds will be
              issued to the original payment method.
            </p>

            <h3>Cancellations</h3>
            <p>
              You may cancel your subscription to any premium plan at any time.
              However, cancellations do not automatically qualify for a refund
              unless within the 7-day refund period mentioned above. To cancel,
              please reach out to{" "}
              <a href="mailto:support@dhandhakarle.com">
                support@dhandhakarle.com
              </a>
              . Upon cancellation, you will retain access to the premium features
              until the end of your billing cycle.
            </p>

            <h3>Non-Refundable Cases</h3>
            <ul>
              <li>
                Refunds will not be provided for subscriptions past the 7-day
                refund period.
              </li>
              <li>
                No refunds will be issued for partial use of the service or if you
                fail to utilize the software during the subscription period.
              </li>
              <li>
                Customized plans or additional services tailored to specific
                business needs are non-refundable unless explicitly agreed upon at
                the time of purchase.
              </li>
            </ul>

            <h3>Contact Us</h3>
            <p>
              If you have any questions about our refund policy or need
              assistance, our support team is available 24/7. Reach out via:
            </p>
            <ul>
              <li>
                Email:{" "}
                <a href="mailto:support@dhandhakarle.com">
                  support@dhandhakarle.com
                </a>
              </li>
              <li>Phone: Available upon request through our contact form.</li>
            </ul>
          </motion.div>
        </motion.div>
      </section>
    </>
  );
};

export default RefundPolicy;